<template>
  <Button
    :class="buttonClasses"
    :disabled="disabled"
    :aria-label="ariaLabel"
    @click="$emit('click')"
    v-bind="$attrs"
    :unstyled="true"
    :style="inlineStyles"
  >
    <slot />
  </Button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';

interface Props {
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  ariaLabel?: string;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  disabled: false,
  fullWidth: false,
});

defineEmits<{
  click: [];
}>();

const buttonClasses = computed(() => [
  'btn',
  `btn-${props.variant}`,
  { 'btn-disabled': props.disabled },
]);

// Inline styles to prevent flash of unstyled content and enforce size constraints
const inlineStyles = computed(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center', // Explicit text centering for extra safety
  padding: '1rem 2rem',
  borderRadius: '0.75rem',
  fontWeight: '600',
  fontSize: '1.125rem',
  minHeight: '44px',
  maxHeight: '60px', // Explicitly constrain maximum height
  minWidth: '44px',
  transition: 'all 300ms ease-in-out',
  border: 'none',
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  width: '100%',
  maxWidth: props.fullWidth ? 'none' : '280px',
  height: 'auto', // Ensure height is auto, not inherited
  overflow: 'hidden', // Prevent content from expanding button
  boxSizing: 'border-box', // Ensure proper box model
  background:
    props.variant === 'primary'
      ? 'linear-gradient(180deg, var(--color-primary-500), var(--color-primary-600))'
      : 'transparent',
  color:
    props.variant === 'primary' ? 'var(--color-arena-surface)' : 'var(--color-arena-text-muted)',
  position: 'relative', // Establish stacking context for ripple effect
  transformOrigin: 'center',
  willChange: 'transform', // Optimize for animations
  ...(props.variant === 'secondary' && {
    border: '2px solid var(--color-arena-border)',
  }),
}));
</script>

<style scoped>
/* Prevent PrimeVue Button from showing default styles before our CSS loads */
:deep(.p-button) {
  all: unset !important;
  box-sizing: border-box !important;
  display: inline-flex !important;
}

/* Alternative approach: immediately override specific PrimeVue button properties */
:deep(button) {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: none !important;
  font: inherit !important;
  color: inherit !important;
  text-decoration: none !important;
  cursor: pointer !important;
  /* Removed outline: none !important to allow accessibility focus styles */
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  box-sizing: border-box !important;
  text-align: center !important; /* Force text centering */
  white-space: nowrap !important; /* Prevent text wrapping that could cause shifts */
}

/* Ensure ripple effect doesn't interfere with text positioning */
:deep([data-pc-name='ripple']) {
  position: absolute !important;
  pointer-events: none !important;
  z-index: 0 !important;
}

/* Ensure button text content stays above ripple */
:deep(button)::before {
  content: '';
  position: relative;
  z-index: 1;
}

.btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Enhanced micro-interactions for buttons */
:deep(button):not(.btn-disabled) {
  /* Subtle hover lift effect */
  transition:
    transform 300ms ease-out,
    filter 300ms ease-out,
    border-color 300ms ease-out,
    background-color 300ms ease-out,
    box-shadow 300ms ease-out;
}

:deep(button):not(.btn-disabled):hover {
  transform: translateY(-1px);
  filter: brightness(1.1);
  border-color: var(--color-energy-primary);
}

:deep(button):not(.btn-disabled):active {
  transform: translateY(0) scale(0.98);
  transition: transform 150ms ease-out;
}

:deep(button):not(.btn-disabled):focus-visible {
  outline: 3px solid var(--color-primary-600);
  outline-offset: 2px;
  box-shadow:
    0 0 0 1px #ffffff,
    0 0 0 4px var(--color-primary-500);
}

/* Click ripple effect */
:deep(button):not(.btn-disabled)::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition:
    width 150ms ease-out,
    height 150ms ease-out;
  pointer-events: none;
  z-index: 1;
}

:deep(button):not(.btn-disabled):active::after {
  width: 100px;
  height: 100px;
  transition:
    width 150ms ease-out,
    height 150ms ease-out;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  :deep(button):not(.btn-disabled):hover {
    transform: none;
  }

  :deep(button):not(.btn-disabled):active {
    transform: none;
  }

  :deep(button):not(.btn-disabled)::after {
    display: none;
  }

  /* Provide alternative feedback for reduced motion users */
  :deep(button):not(.btn-disabled):active {
    background: var(--color-primary-700) !important;
  }
}
</style>
